<template lang="pug">
.text-field-math
  .text-field-math__relative
    input.text-field-math__relative__input(
      ref='textfield',
      inputmode='none',
      @focus='onFocus',
      @click='onFocus',
      @blur='onBlur',
      :style='textPosition',
      readonly
    )
    .text-field-math__relative__katex(ref='katexfield', @click='onClick', :style='katexPosition')
      .text-field-math__relative__katex__text(v-html='contentKatex')
  .text-field-math__background(:data-show='isShow')
    .text-field-math__background__handle(
      ref='handle',
      @mousedown='dragAttachmentStart',
      @mouseup='dragAttachmentEnd',
      @touchstart='touchAttachmentStart',
      @touchend='touchAttachmentEnd'
    )
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import { KatexPosition, TextPosition } from '@/models/textFieldKatexStyle'
import katex from 'katex'

const HANDLE_HEIGHT = 60

/**
 * 数式キーボード付き入力フィールド
 * フィールドクリックでキーボードが表示される
 */
@Component
export default class TextFieldMath extends Vue {
  private attachment: any | null = null
  private katexPosition: KatexPosition = {
    left: 0,
    top: 0,
    width: 0,
    color: 'rgba(0, 0, 0, 0.6)',
  }
  private textPosition: TextPosition = {
    height: 0,
  }
  private textObserver: any
  private katexObserver: any

  private isShow = false
  private shiftX = 0
  private shiftY = 0

  @Prop()
  keyboardName!: string

  @Prop({ default: '' })
  value!: string

  @Prop({ default: false })
  readonly?: boolean

  @Ref() textfield!: HTMLInputElement
  @Ref() katexfield!: HTMLElement

  @Ref() handle!: HTMLElement

  @Watch('attachment.state.isActive')
  private onChange() {
    this.isShow = this.attachment.state.isActive
    if (this.attachment.state.isActive) {
      // 移動用のハンドルブロックをアタッチメントの上部に表示
      const keyboardInner = document.getElementById('keyboard-container-inner')
      if (keyboardInner) {
        // ぴったり上部に表示するためにアタッチメントのradiusとmarginを0にする
        keyboardInner.style.borderRadius = '0'
        keyboardInner.style.margin = '0'
        const keyboardRect = keyboardInner.getBoundingClientRect()
        this.handle.style.width = `${keyboardRect.width}px`
        this.handle.style.left = `${keyboardRect.left}px`
        // ハンドルブロックの高さ分上に表示する
        this.handle.style.top = `${keyboardRect.top - HANDLE_HEIGHT}px`
      }
    } else {
      // アタッチメント位置をリセット
      const keyboard = document.getElementById('keyboard-container')
      if (keyboard) {
        keyboard.style.left = ''
        keyboard.style.top = ''
      }
    }
  }

  get contentKatex(): string {
    if (!this.value) {
      return ''
    } else {
      return katex.renderToString(this.value.replace(/\\placeholder\{?\}?/g, '⬚'), {
        throwOnError: false,
        displayMode: false,
      })
    }
  }

  private mounted() {
    // アタッチメント (WebIMEとやり取りするためのモジュール)を設定
    this.attachment = Vue.prototype.$webime.createTextFieldAttachment({
      keyboardName: this.keyboardName,
      singleLine: true,
    })

    // IMEによるテキスト内容変更時
    this.attachment.on('text-change', (evt: any) => {
      if (this.readonly) {
        evt.preventDefault(true)
        return
      }
      this.$emit('input', evt.text)
    })

    this.textObserver = new (window as any).ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.katexPosition = {
          width: `${this.textfield.offsetWidth - 4}px`,
          left: `${this.textfield.offsetLeft + 2}px`,
          top: `${this.textfield.offsetTop + 2}px`,
          color: 'rgb(0, 0, 0)',
        }
      })
    })
    this.textObserver.observe(this.textfield)

    this.katexObserver = new (window as any).ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.textPosition = {
          height: `${(this.katexfield.offsetHeight > 41 ? this.katexfield.offsetHeight : 41) + 4}px`,
        }
      })
    })
    this.katexObserver.observe(this.katexfield)
  }

  private beforeDestroy() {
    // アタッチメントを解放
    Vue.prototype.$webime.releaseTextFieldAttachment(this.attachment)
    this.attachment = null
    this.textObserver.disconnect()
    this.katexObserver.disconnect()
  }

  private onClick() {
    if (this.readonly) return
    this.textfield.focus()
    this.onFocus()
  }

  private onFocus() {
    // フォーカスを通知
    if (this.readonly) return
    this.attachment.focus()
    this.attachment.setLatex(this.value)
    this.textfield.classList.add('use-webime')
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
    const rect = this.textfield.getBoundingClientRect()
    window.scrollTo({
      top: rect.top + window.pageYOffset + rect.height / 2 - vh / 2,
      behavior: 'smooth',
    })
  }

  private onBlur() {
    this.textfield.classList.remove('use-webime')
    this.attachment.blur()
  }

  /**
   * マウスドラッグでアタッチメントを移動する
   */
  // マウスクリック時
  private dragAttachmentStart(e: MouseEvent) {
    // 他のマウスイベントをキャンセル
    e.preventDefault()
    // ハンドルブロック端からマウス位置への距離を取得
    const rect = this.handle.getBoundingClientRect()
    this.shiftX = e.clientX - rect.left
    this.shiftY = e.clientY - rect.top
    // マウス移動イベントを登録
    window.addEventListener('mousemove', this.moveAttachment)
  }
  // マウスを離した時
  private dragAttachmentEnd() {
    // マウス移動イベントを解除
    window.removeEventListener('mousemove', this.moveAttachment)
  }
  // マウス移動時のハンドルブロックとアタッチメント移動イベント
  private moveAttachment(e: MouseEvent) {
    // ハンドルブロックのleft/topにマウス位置から距離分を引いた値を設定する
    const left = e.clientX - this.shiftX
    const top = e.clientY - this.shiftY
    this.handle.style.left = left + 'px'
    this.handle.style.top = top + 'px'
    // アタッチメントはハンドルブロックの高さ分下の位置に設定する
    const keyboard = document.getElementById('keyboard-container')
    if (keyboard) {
      keyboard.style.left = left + 'px'
      keyboard.style.top = top + HANDLE_HEIGHT + 'px'
    }
  }

  /**
   * タッチイベントでアタッチメントを移動する
   */
  // 画面タッチ時
  private touchAttachmentStart(e: TouchEvent) {
    // 他のタッチイベントをキャンセル
    e.preventDefault()
    // ハンドルブロック端からタッチ位置への距離を取得
    const rect = this.handle.getBoundingClientRect()
    this.shiftX = e.touches[0].clientX - rect.left
    this.shiftY = e.touches[0].clientY - rect.top
    // タッチ移動イベントを登録
    window.addEventListener('touchmove', this.touchMoveAttachment)
  }
  // 画面タッチを離した時
  private touchAttachmentEnd() {
    // タッチ移動イベントを解除
    window.removeEventListener('touchmove', this.touchMoveAttachment)
  }
  // タッチ移動時のハンドルブロックとアタッチメント移動イベント
  private touchMoveAttachment(e: TouchEvent) {
    // ハンドルブロックのleft/topにタッチ位置から距離分を引いた値を設定する
    const left = e.touches[0].clientX - this.shiftX
    const top = e.touches[0].clientY - this.shiftY
    this.handle.style.left = left + 'px'
    this.handle.style.top = top + 'px'
    // アタッチメントはハンドルブロックの高さ分下の位置に設定する
    const keyboard = document.getElementById('keyboard-container')
    if (keyboard) {
      keyboard.style.left = left + 'px'
      keyboard.style.top = top + HANDLE_HEIGHT + 'px'
    }
  }
}
</script>

<style lang="stylus" scoped>
.text-field-math
  &__relative
    position relative

    &__input
      width 10.5rem
      padding 5px
      border-radius 4px
      appearance none
      -webkit-appearance none
      -moz-appearance none

    &__katex
      position absolute
      display flex
      border-radius 4px
      background-color var(--color-input-field-keyboard__background)
      font-size 16px
      overflow auto
      z-index 5
      white-space nowrap
      &::-webkit-scrollbar
        width 8px
        height 8px
      &::-webkit-scrollbar-track
        background var(--color-input-field-keyboard__background)
        border-left solid 1px var(--color-input-field-keyboard-scrollbar__border)
      &::-webkit-scrollbar-thumb
        background var(--color-input-field-keyboard-scrollbar__background)
        border-radius 8px
        box-shadow inset 0 0 0 2px var(--color-input-field-keyboard__background)

      &__text
        flex 1 1 auto
        padding 8px

  &__background
    display none
    position fixed
    left 0
    top 0
    width 100%
    height 100%
    z-index 100
    &__handle
      display flex
      height 60px
      position absolute
      background var(--color-input-field-keyboard-handle__background)
      z-index 200
      border-radius 3px 3px 0 0
    &[data-show='true']
      display block
</style>
